import { PASSWORD_MIN_LENGTH } from '@bartizo/shared';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { changePassword } from '../api/auth.js';
import { isApiError } from '../api/client.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { TextField } from '../ui/Field.js';

/**
 * Changing your own password (knowledge doc section 17).
 *
 * The confirmation field is checked here rather than by the API, because a typo in
 * a new password is a client-side mistake and the round trip would tell the person
 * nothing they could not be told at once.
 */

export function PasswordCard(): ReactNode {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirmation.length > 0 && next !== confirmation;

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    if (next !== confirmation) {
      setError('The two new passwords do not match.');
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirmation('');
      setNotice(
        result.otherSessionsEnded === 0
          ? 'Your password has been changed.'
          : `Your password has been changed, and ${String(result.otherSessionsEnded)} other ${
              result.otherSessionsEnded === 1 ? 'session was' : 'sessions were'
            } signed out.`,
      );
    } catch (caught: unknown) {
      setError(isApiError(caught) ? caught.message : 'Could not change your password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Password"
      description="Changing it signs out every other browser you are signed in on."
    >
      {notice !== null && <Alert tone="success">{notice}</Alert>}
      {error !== null && <Alert tone="danger">{error}</Alert>}

      <form onSubmit={(event) => void submit(event)} className="stack">
        <TextField
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(event) => {
            setCurrent(event.target.value);
          }}
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          hint={`At least ${String(PASSWORD_MIN_LENGTH)} characters. A memorable phrase beats a short tangle of symbols.`}
          required
          minLength={PASSWORD_MIN_LENGTH}
          value={next}
          onChange={(event) => {
            setNext(event.target.value);
          }}
        />
        <TextField
          label="New password again"
          type="password"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN_LENGTH}
          error={mismatch ? 'These do not match.' : null}
          value={confirmation}
          onChange={(event) => {
            setConfirmation(event.target.value);
          }}
        />

        <div className="row">
          <Button type="submit" variant="primary" busy={busy} busyLabel="Saving…">
            Change password
          </Button>
        </div>
      </form>
    </Card>
  );
}
